import { type components } from "@njbsoft/types/customer";
import { isBrandColor } from "@/lib/brand";
import { isPortalSlug } from "@/lib/portal-slug";
import { uuidParam } from "@/lib/search-params";

type CustomerPortalBranding = components["schemas"]["CustomerPortalBranding"];
type CustomerMembership = components["schemas"]["CustomerMembership"];

/**
 * The remembered utility: the ACTIVE membership's slug, public branding and
 * account, kept apart from the session store so it survives sign-out. It
 * brands the next visit's first frame (`public/theme-init.js` reads it before
 * any bundle loads) and the pending and access-denied windows after sign-in,
 * and its account is the one the next sign-in at that utility lands in.
 */
export const PORTAL_MEMORY_KEY = "njbsoft-customer-portal";

const PORTAL_MEMORY_VERSION = 1;

export interface RememberedPortal {
  slug: string;
  branding: CustomerPortalBranding;
  /** The active membership's `account_id`; absent from an entry written without one. */
  accountId?: string;
}

/**
 * Stores the entry. A storage that refuses the write (quota exceeded, a
 * private mode that throws) leaves nothing remembered rather than throwing
 * into the layout's effect.
 */
export function rememberPortal(entry: RememberedPortal): void {
  try {
    localStorage.setItem(
      PORTAL_MEMORY_KEY,
      JSON.stringify({
        v: PORTAL_MEMORY_VERSION,
        slug: entry.slug,
        branding: entry.branding,
        accountId: entry.accountId,
      }),
    );
  } catch {
    forgetPortal();
  }
}

/**
 * The remembered utility, or null when nothing is stored or the entry fails
 * any check: the version, the slug shape, the brand color, a non-empty org
 * name, a boolean `has_logo`, and each nullable field present as a string or
 * null. A malformed entry is never repaired; it reads as nothing. The account
 * id is only a preference for the next sign-in: when it is absent or not a
 * uuid-shaped string it is left out and the rest still reads, just as
 * `public/theme-init.js`, which never reads it, still paints. Never throws.
 */
export function readRememberedPortal(): RememberedPortal | null {
  try {
    const raw = localStorage.getItem(PORTAL_MEMORY_KEY);
    return raw === null ? null : parseRememberedPortal(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Clears the entry; a storage that throws has nothing to clear. Never throws. */
export function forgetPortal(): void {
  try {
    localStorage.removeItem(PORTAL_MEMORY_KEY);
  } catch {
    // A storage that refuses access refuses the read too, which reads as null.
  }
}

/** A membership's public branding. The generated type marks the five nullable
 *  fields optional; each is normalized to null so the stored entry carries
 *  every key the strict read requires. */
export function brandingFromMembership(m: CustomerMembership): CustomerPortalBranding {
  return {
    org_name: m.org_name,
    program_name: m.program_name ?? null,
    brand_color: m.brand_color,
    has_logo: m.has_logo,
    program_office_line: m.program_office_line ?? null,
    contact_phone: m.contact_phone ?? null,
    contact_email: m.contact_email ?? null,
    website_url: m.website_url ?? null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function parseRememberedPortal(value: unknown): RememberedPortal | null {
  if (!isRecord(value) || value.v !== PORTAL_MEMORY_VERSION) return null;
  const { slug, branding, accountId } = value;
  if (!isPortalSlug(slug) || !isRecord(branding)) return null;
  const {
    org_name,
    program_name,
    brand_color,
    has_logo,
    program_office_line,
    contact_phone,
    contact_email,
    website_url,
  } = branding;
  if (typeof org_name !== "string" || org_name === "") return null;
  if (!isBrandColor(brand_color) || typeof has_logo !== "boolean") return null;
  if (
    !isNullableString(program_name) ||
    !isNullableString(program_office_line) ||
    !isNullableString(contact_phone) ||
    !isNullableString(contact_email) ||
    !isNullableString(website_url)
  ) {
    return null;
  }
  const remembered: RememberedPortal = {
    slug,
    branding: {
      org_name,
      program_name,
      brand_color,
      has_logo,
      program_office_line,
      contact_phone,
      contact_email,
      website_url,
    },
  };
  const account = uuidParam(accountId);
  return account === undefined ? remembered : { ...remembered, accountId: account };
}
